/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import { type SwotCategory, type TimeHorizon, type SectionType, type GenerationRun, GenerationRunStatus } from '@g17eco/types/insights-synthesis';
import { DATE, formatDateUTC } from '../../../../../utils/date';

export interface FilterOption<T = string> {
  label: string;
  value: T;
}

export const TIME_HORIZON_OPTIONS: FilterOption<'all' | TimeHorizon>[] = [
  { label: 'All', value: 'all' },
  { label: 'Immediate (<12mo)', value: 'immediate' },
  { label: '1-3 Years', value: 'short_term' },
  { label: '5+ Years', value: 'medium_term' },
];

export const PILLAR_OPTIONS: FilterOption<'all' | SectionType>[] = [
  { label: 'ALL', value: 'all' },
  { label: 'ENVIRONMENT', value: 'environment' },
  { label: 'ECONOMIC', value: 'economic' },
  { label: 'SOCIAL', value: 'social' },
  { label: 'GOVERNANCE', value: 'governance' },
];

export const SWOT_CATEGORY_OPTIONS: FilterOption<'all' | SwotCategory>[] = [
  { label: 'ALL', value: 'all' },
  { label: 'STRENGTH', value: 'strength' },
  { label: 'WEAKNESS', value: 'weakness' },
  { label: 'OPPORTUNITY', value: 'opportunity' },
  { label: 'THREAT', value: 'threat' },
];

// Badge color mapping for pillars
export const PILLAR_COLORS: Record<SectionType, string> = {
  environment: 'success', // green
  social: 'primary', // blue
  governance: 'warning', // orange
  economic: 'info', // purple/cyan
};

// Badge color mapping for SWOT categories
export const SWOT_COLORS: Record<SwotCategory, string> = {
  strength: 'success',
  weakness: 'danger',
  opportunity: 'info',
  threat: 'warning',
};

// Time horizon display labels
export const TIME_HORIZON_LABELS: Record<TimeHorizon, string> = {
  immediate: '<12 Months',
  short_term: '1-3 Years',
  medium_term: '3-5 Years',
  long_term: '5+ Years',
};

// Control level display labels
export const CONTROL_LEVEL_LABELS: Record<'low' | 'medium' | 'high', string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export enum IntelligenceView {
  Intelligence = 'intelligence',
  Priorities = 'priorities',
  Recommendations = 'recommendations',
}

export function getIntelligenceViewDetails(view: IntelligenceView) {
  if (view === IntelligenceView.Intelligence) {
    return {
      menuLabel: <><i className='fas fa-lightbulb me-2' /> Insights Intelligence</>,
      title: 'Insights Intelligence',
      subtitle: 'Explore AI-generated insights with priority scores, impact ratings, and actionable recommendations',
    };
  }
  if (view === IntelligenceView.Priorities) {
    return {
      menuLabel: <><i className='fas fa-chart-pie me-2' /> Prioritization</>,
      title: 'Prioritization & Impact',
      subtitle: 'Charts and heatmaps across impact, SWOT, EESG and time dimensions to identify critical focus areas',
    };
  }
  return {
    menuLabel: <><i className='fas fa-tasks me-2' /> Recommendations</>,
    title: 'Recommendations & Actions',
    subtitle: 'Recommendations ranked by impact and effort to guide implementation sequencing and resource allocation',
  };
}

export function getRunLabel(run: GenerationRun) {
  const effective = getEffectiveRunStatus(run, 10 * 60 * 1000); // 10 minutes
  const statusLabel =
    effective === 'completed'
      ? ''
      : effective === 'failed'
        ? ' (Failed)'
        : effective === 'timed_out'
          ? ' (Timed Out)'
          : ' (In Progress)';
  return {
    label: `Generated ${formatDateUTC(run.createdAt, DATE.HUMANIZE)}${statusLabel}`,
    value: run.batchId,
  };

}

type EffectiveRunStatus = 'in_progress' | 'timed_out' | 'completed' | 'failed';

function getEffectiveRunStatus(run: GenerationRun, timeoutMs: number): EffectiveRunStatus {
  const isInProgress =
    run.status === GenerationRunStatus.Pending || run.status === GenerationRunStatus.Processing;
  if (!isInProgress) {
    return run.status === GenerationRunStatus.Failed ? 'failed' : 'completed';
  }
  const runAge = Date.now() - new Date(run.createdAt).getTime();
  return runAge >= timeoutMs ? 'timed_out' : 'in_progress';
}

